import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
    AlertCircle,
    X,
    RefreshCw,
} from 'lucide-react';
import EmployeeSearch from '../../components/common/EmployeeSearch';
import {
    fetchEmployeeLeaveSummary,
    selectLeaveSummary,
    selectLeaveTypeList,
    selectLeaveSummaryLoading,
    selectLeaveError,
    clearLeaveError,
} from '../../store/slices/leaveSlice';
import LeaveService from '../../services/LeaveService';

// ─── Main Component ─────────────────────────────────────────────────────
export default function LeaveBalance() {
    const dispatch = useDispatch();
    const store = useStore();

    // ── Redux State ────────────────────────────────────────────────────
    const leaveSummary = useSelector(selectLeaveSummary);
    const leaveTypeList = useSelector(selectLeaveTypeList);
    const isLoadingSummary = useSelector(selectLeaveSummaryLoading);
    const error = useSelector(selectLeaveError);

    // ── Local State ────────────────────────────────────────────────────
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState({
        employeeId: '__ALL__',
        name: 'All Employees',
        isAll: true,
    });
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    // ── Fetch Employees ────────────────────────────────────────────────
    const fetchEmployees = useCallback(async () => {
        setLoadingEmployees(true);

        try {
            const state = store.getState();
            const reduxEmployees = state.employees?.employees;

            if (
                reduxEmployees &&
                Array.isArray(reduxEmployees) &&
                reduxEmployees.length > 0
            ) {
                setEmployees(reduxEmployees);
                return;
            }

            const response =
                (await LeaveService.GetEmployees?.()) ||
                (await fetch('/api/employees').then((r) => r.json()));

            const empList =
                response?.ResultSet ||
                response?.data ||
                response ||
                [];

            setEmployees(Array.isArray(empList) ? empList : []);
        } catch (error) {
            console.error(
                '[LeaveBalance] Failed to fetch employees:',
                error
            );

            setEmployees([]);
        } finally {
            setLoadingEmployees(false);
        }
    }, [store]);

    // ── Fetch Leave Summary ───────────────────────────────────────────
    const refreshSummary = useCallback(() => {
        dispatch(fetchEmployeeLeaveSummary());
    }, [dispatch]);

    // ── Initial Load ──────────────────────────────────────────────────
    useEffect(() => {
        fetchEmployees();
        refreshSummary();
    }, [fetchEmployees, refreshSummary]);

    // ── Employee options for EmployeeSearch ───────────────────────────
    const employeeOptions = useMemo(() => {
        if (!employees || !Array.isArray(employees)) return [];

        return employees
            .map((emp) => {
                const serviceNo =
                    emp.p_ced_service_no ||
                    emp.HED_SERVICE_NO ||
                    emp.HED_CED_SERVICE_NO ||
                    emp.serviceNo ||
                    '';
                const name =
                    emp.p_report_name ||
                    emp.HED_REPORT_NAME ||
                    emp.reportName ||
                    `${emp.p_first_name || emp.HED_FIRST_NAME || ''} ${emp.p_last_name || emp.HED_LAST_NAME || ''}`.trim() ||
                    'Unnamed Employee';

                return {
                    employeeId: String(
                        serviceNo || emp.HED_EMPLOYEE_ID || emp.employeeId || ''
                    ),
                    serviceNo: String(serviceNo),
                    name,
                    department:
                        emp.p_department_name ||
                        emp.HDD_DEPARTMENT_NAME ||
                        '',
                };
            })
            .filter((e) => e.employeeId);
    }, [employees]);

    // ── Filter by selected employee ───────────────────────────────────
    const filteredData = useMemo(() => {
        if (!leaveSummary || leaveSummary.length === 0) return [];

        if (!selectedEmployee || selectedEmployee.isAll) return leaveSummary;

        return leaveSummary.filter(
            (item) => item.serviceNo === selectedEmployee.serviceNo
        );
    }, [leaveSummary, selectedEmployee]);

    // ── Handlers ──────────────────────────────────────────────────────
    const handleEmployeeSelect = (employee) => {
        if (!employee) {
            setSelectedEmployee({
                employeeId: '__ALL__',
                name: 'All Employees',
                isAll: true,
            });
        } else {
            setSelectedEmployee(employee);
        }
        dispatch(clearLeaveError());
    };

    const handleRefresh = () => refreshSummary();

    const isLoading = loadingEmployees || isLoadingSummary;

    // ── Dynamic Columns (type-wise) ───────────────────────────────────
    const columns = useMemo(() => {
        const baseCols = [
            {
                key: 'employeeName',
                label: 'Employee',
                width: '200px',
                className: 'font-semibold text-gray-900',
                render: (row) => row?.employeeName || '—',
            },
            {
                key: 'serviceNo',
                label: 'Service No',
                width: '130px',
                className: 'text-gray-500',
                render: (row) => row?.serviceNo || '—',
            },
            {
                key: 'leaveYear',
                label: 'Year',
                width: '80px',
                align: 'center',
                className: 'text-gray-500',
                render: (row) => row?.leaveYear || '—',
            },
        ];

        const typeCols = (leaveTypeList || [])
            .filter(
                (type) =>
                    type.code !== 'UNK' &&
                    type.name !== 'UNK'
            )
            .map((type) => ({
                key: `type_${type.code}`,
                label: type.name,
                width: '120px',
                align: 'center',
                render: (row) => {
                    const t = row?.types?.[type.code];

                    if (!t) {
                        return (
                            <span className="text-xs text-gray-300">
                                —
                            </span>
                        );
                    }

                    return (
                        <span className="text-emerald-600 font-semibold whitespace-nowrap">
                            {(t.balance || 0).toFixed(1)}
                        </span>
                    );
                },
            }));

        const tailCols = [
            {
                key: 'totalUsed',
                label: 'Total Used',
                width: '110px',
                align: 'center',
                className: 'text-gray-600',
                render: (row) => row?.totalUsed?.toFixed(2) || '0.00',
            },
            {
                key: 'totalBalance',
                label: 'Total Balance',
                width: '140px',
                align: 'center',
                render: (row) => (
                    <span className="text-emerald-600 font-semibold whitespace-nowrap">
                        {(row?.totalBalance || 0).toFixed(2)}
                    </span>
                ),
            },
        ];

        return [...baseCols, ...typeCols, ...tailCols];
    }, [leaveTypeList]);

    const alignClass = (align) =>
        align === 'center'
            ? 'text-center'
            : align === 'right'
                ? 'text-right'
                : 'text-left';

    // ── Render ────────────────────────────────────────────────────────
    return (
        <div className="p-6 space-y-4">
            {/* ── Filters ────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[220px] max-w-md">
                    <EmployeeSearch
                        value={selectedEmployee}
                        onSelect={handleEmployeeSelect}
                        employees={employeeOptions}
                        loading={loadingEmployees}
                        showAllOption
                    />
                </div>

                <div>
                    <button
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-surface-50 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw
                            size={15}
                            className={isLoading ? 'animate-spin' : ''}
                        />
                        Refresh
                    </button>
                </div>
            </div>

            {/* ── Error Display ──────────────────────────────────────── */}
            {error && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 border border-red-200 flex items-center gap-2">
                    <AlertCircle size={14} />

                    {error}

                    <button
                        onClick={() => dispatch(clearLeaveError())}
                        className="ml-auto text-red-500 hover:text-red-700"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* ── Table (page scroll, sticky header) ─────────────────── */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                {isLoading ? (
                    <div className="py-20 text-center text-sm text-gray-400">
                        Loading leave balances...
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="px-5 py-10 text-center text-sm text-gray-400">
                        No leave balances found.
                    </div>
                ) : (
                    <table
                        className="w-full border-collapse text-sm"
                        style={{ minWidth: 700 }}
                    >
                        <thead className="sticky -top-6 z-10 bg-gray-50 shadow-[0_1px_0_0_rgb(229_231_235)]">
                            <tr className="bg-gray-50">
                                {columns.map((col) => (
                                    <th
                                        key={col.key}
                                        style={{ width: col.width }}
                                        className={`whitespace-nowrap bg-gray-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 ${alignClass(col.align)}`}
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((row, index) => (
                                <tr
                                    key={row.serviceNo || `row-${index}`}
                                    className={index % 2 === 1 ? 'bg-surface-50/40' : ''}
                                >
                                    {columns.map((col) => (
                                        <td
                                            key={col.key}
                                            className={`px-5 py-4 ${alignClass(col.align)} ${col.className || ''}`}
                                        >
                                            {col.render
                                                ? col.render(row)
                                                : (row[col.key] ?? '-')}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Summary footer ─────────────────────────────────────── */}
            {!isLoading && !error && filteredData.length > 0 && (
                <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4">
                    <div className="text-sm text-gray-500">
                        Showing{' '}
                        <strong className="text-gray-800">
                            {filteredData.length}
                        </strong>{' '}
                        {filteredData.length === 1 ? 'entry' : 'entries'}
                    </div>
                </div>
            )}
        </div>
    );
}